/**
 * Klaus cross-promo — "Klaus kann KI", the sibling product from the team behind Dieter,
 * helping small businesses with the EU AI Act.
 *
 * Four touchpoints promote it inside the Dieter app. They all funnel through `openKlaus()`
 * so every click carries the same UTM campaign and is attributable per touchpoint:
 *
 *  - `banner`        NotificationBanner entry in the dashboard navigation
 *  - `modal`         Launch modal, dispatched via the MessageContext queue
 *  - `task`          SimpleProcess task `process-klaus-ai-act-check` (seeded, opens via
 *                    `callbackOnComplete` — that URL is built in the seed data, not here)
 *  - `context-panel` Panel above the finished privacy policy, shown when the user declared
 *                    AI services in the questionnaire
 */

export const KLAUS_BASE_URL = 'https://klaus-ki.eu/';

export type KlausTouchpoint = 'banner' | 'modal' | 'task' | 'context-panel';

/**
 * Service category keys (see `snippetCategoryKeys.ts`) whose presence in the company's
 * PersistentState is treated as "this company uses AI services". Any non-empty selection
 * counts — we deliberately do not differentiate by provider.
 */
export const KLAUS_AI_CATEGORY_KEYS = ['API_PROVIDER', 'KI_TOOL', 'CHATBOT'] as const;

/**
 * The app whose questionnaire owns the service selection — and therefore the only document the
 * context panel appears above. Mirrors SERVICE_SELECTION_APP_ID on the server.
 */
export const SERVICE_SELECTION_APP_ID = 'app-dse-privacy-policy-create';

/**
 * Seeded tasks that are Klaus cross-promo rather than Dieter work. They get Klaus branding in
 * the task list so a user can see at a glance that this one leads to the sibling product.
 * Matched on `persistentId` — the same approach ProcessItem already uses for the basic/premium
 * upsell tasks.
 *
 * Purely cosmetic. Whether the task reaches the user at all is decided server-side by
 * `CROSS_PROMO_TASK_IDS` in dieter-server's schema/dieter/task/Task.ts — keep the two in sync.
 */
const KLAUS_TASK_PERSISTENT_IDS = ['process-klaus-ai-act-check'];

export function isKlausTask(persistentId: string | undefined): boolean {
  return !!persistentId && KLAUS_TASK_PERSISTENT_IDS.includes(persistentId);
}

/**
 * How settled in a user has to be before the launch modal may interrupt them.
 *
 * A brand-new account meeting Dieter for the first time should not be pushed towards a sibling
 * product before they have found their way around. The banner and the task still reach them —
 * those sit in the periphery; the modal is the one that takes over the screen.
 */
export const KLAUS_MODAL_MIN_ACCOUNT_AGE_DAYS = 7;

export function accountIsOldEnoughForKlausModal(createdAt: string | undefined): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  return Date.now() - created >= KLAUS_MODAL_MIN_ACCOUNT_AGE_DAYS * 24 * 60 * 60 * 1000;
}

export function klausUrl(content: KlausTouchpoint): string {
  const params = new URLSearchParams({
    utm_source: 'dieter-app',
    utm_medium: 'cross-promo',
    utm_campaign: 'klaus-launch',
    utm_content: content,
  });
  return `${KLAUS_BASE_URL}?${params}`;
}

/**
 * Öffnet Klaus in einem neuen Tab — über /zu-klaus, also ohne neuen Login
 * (dev/klaus-sync/addendum-v1.5.md). Ist die Kopplung nicht eingerichtet,
 * leitet /zu-klaus auf die Klaus-Website mit den UTM-Parametern weiter.
 */
export function openKlaus(content: KlausTouchpoint) {
  if (window.dataLayer) {
    window.dataLayer.push({ event: 'klaus_cta_click', klaus_touchpoint: content });
  }
  window.open(`/zu-klaus?src=${encodeURIComponent(content)}`, '_blank', 'noopener,noreferrer');
}

export function trackKlausImpression(content: KlausTouchpoint) {
  if (window.dataLayer) {
    window.dataLayer.push({ event: 'klaus_impression', klaus_touchpoint: content });
  }
}

/**
 * Whether the Klaus cross-promo may be shown at all.
 *
 * Klaus is our own sibling product, so wherever the app runs under a partner's brand it has no
 * business being advertised — neither to the partner nor to the companies the partner manages.
 * A resolvable whitelabel config is the signal for that, and it is checked from whichever of its
 * three sources produced it (share token, active company, host-based matching).
 *
 * The `Whitelabel` company feature is a second, independent signal: sub-tenants inherit the
 * feature from their principal but not its `whitelabelConfigId` (see `createTenantCompany` on the
 * server), so on the core domain the config alone would not catch them.
 *
 * Fails closed while the config is still loading — a promo that flashes up and then vanishes is
 * worse than one that arrives a moment late.
 */
export function klausPromoAllowed(opts: {
  whitelabelConfig: unknown;
  whitelabelLoading: boolean;
  hasWhitelabelFeature: boolean | undefined;
}): boolean {
  return !opts.whitelabelLoading && !opts.whitelabelConfig && !opts.hasWhitelabelFeature;
}
